import React, { useState, useRef, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { privateAxios } from "../utils/axios";
import toast from "react-hot-toast";

function AddBranch({ addBranchModalSetting, handlePageUpdate }) {
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    receiverName: "",
    receiverNumber: "",
    address: "",
    branchCode: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(true);
  const cancelButtonRef = useRef(null);

  const nameRef = useRef();
  const numberRef = useRef();
  const receiverNameRef = useRef();
  const receiverNumberRef = useRef();
  const addressRef = useRef();
  const branchCodeRef = useRef();

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) {
          return "Branch name is required";
        } else if (value.trim().length < 2) {
          return "Branch name must be at least 2 characters";
        } else if (value.trim().length > 100) {
          return "Branch name must be less than 100 characters";
        }
        break;
      case "number":
        if (!value.trim()) {
          return "Number is required";
        } else if (value.trim().length < 3) {
          return "Number must be at least 3 characters";
        }
        break;
      case "receiverName":
        if (!value.trim()) {
          return "Receiver name is required";
        } else if (value.trim().length < 2) {
          return "Receiver name must be at least 2 characters";
        }
        break;
      case "receiverNumber":
        if (!value.trim()) {
          return "Receiver number is required";
        } else if (value.trim().length < 3) {
          return "Receiver number must be at least 3 characters";
        }
        break;
      case "address":
        if (value && value.trim().length > 200) {
          return "Address must be less than 200 characters";
        }
        break;
      case "branchCode":
        if (value && value.trim().length > 50) {
          return "Branch code must be less than 50 characters";
        }
        break;
      default:
        return "";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleKeyDown = (e, field) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const value = formData[field]?.trim() || "";
      const error = validateField(field, value);

      if (error) {
        setErrors((prev) => ({ ...prev, [field]: error }));
        return;
      }

      setErrors((prev) => ({ ...prev, [field]: "" }));

      switch (field) {
        case "name":
          numberRef.current.focus();
          break;
        case "number":
          receiverNameRef.current.focus();
          break;
        case "receiverName":
          receiverNumberRef.current.focus();
          break;
        case "receiverNumber":
          addressRef.current.focus();
          break;
        case "address":
          branchCodeRef.current.focus();
          break;
        case "branchCode":
          const formErrors = Object.keys(formData).reduce((acc, key) => {
            const error = validateField(key, formData[key]);
            if (error) acc[key] = error;
            return acc;
          }, {});

          if (Object.keys(formErrors).length === 0) {
            handleSubmit(e);
          } else {
            setErrors(formErrors);
          }
          break;
        default:
          break;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = Object.keys(formData).reduce((acc, key) => {
      const error = validateField(key, formData[key]);
      if (error) acc[key] = error;
      return acc;
    }, {});

    if (Object.keys(formErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const cleanedFormData = Object.entries(formData).reduce(
          (acc, [key, value]) => {
            if ((key === "address" || key === "branchCode") && !value.trim()) {
              return acc;
            }
            acc[key] = value;
            return acc;
          },
          {}
        );

        const response = await privateAxios.post("/branches", cleanedFormData);
        if (response.data.success) {
          toast.success("Branch added successfully!");
          handlePageUpdate();
          addBranchModalSetting();
        } else {
          toast.error(response.data.message || "Failed to add branch");
        }
      } catch (error) {
        console.error("Error adding branch:", error);
        toast.error(error.response?.data?.message || "Error adding branch");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(formErrors);
      toast.error("Please fix the validation errors");
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-gray-100">
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                  onClick={() => addBranchModalSetting()}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="bg-gradient-to-b from-white to-gray-50 px-6 pt-6 pb-4 sm:p-6">
                  <div className="w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-2xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100"
                    >
                      Add New Branch
                    </Dialog.Title>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label
                          htmlFor="name"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Branch Name *
                        </label>
                        <input
                          ref={nameRef}
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "name")}
                          placeholder="Ex. Dhaka Branch"
                          className={`bg-white border-2 ${
                            errors.name
                              ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                          } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="number"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Number *
                        </label>
                        <input
                          ref={numberRef}
                          type="text"
                          name="number"
                          id="number"
                          value={formData.number}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "number")}
                          placeholder="Ex. 01571000000"
                          className={`bg-white border-2 ${
                            errors.number
                              ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                          } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                        />
                        {errors.number && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.number}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="receiverName"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Receiver Name *
                        </label>
                        <input
                          ref={receiverNameRef}
                          type="text"
                          name="receiverName"
                          id="receiverName"
                          value={formData.receiverName}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "receiverName")}
                          placeholder="Ex. John Doe"
                          className={`bg-white border-2 ${
                            errors.receiverName
                              ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                          } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                        />
                        {errors.receiverName && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.receiverName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="receiverNumber"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Receiver Number *
                        </label>
                        <input
                          ref={receiverNumberRef}
                          type="text"
                          name="receiverNumber"
                          id="receiverNumber"
                          value={formData.receiverNumber}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "receiverNumber")}
                          placeholder="Ex. 01571000000"
                          className={`bg-white border-2 ${
                            errors.receiverNumber
                              ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                          } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                        />
                        {errors.receiverNumber && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.receiverNumber}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="address"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Address
                        </label>
                        <textarea
                          ref={addressRef}
                          name="address"
                          id="address"
                          value={formData.address}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "address")}
                          placeholder="Ex. 123 Main Street, City"
                          rows="2"
                          className={`bg-white border-2 ${
                            errors.address
                              ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                          } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                        />
                        {errors.address && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.address}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="branchCode"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Branch Code
                        </label>
                        <input
                          ref={branchCodeRef}
                          type="text"
                          name="branchCode"
                          id="branchCode"
                          value={formData.branchCode}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "branchCode")}
                          placeholder="Ex. BR-001"
                          className={`bg-white border-2 ${
                            errors.branchCode
                              ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                          } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                        />
                        {errors.branchCode && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.branchCode}
                          </p>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl border-t border-gray-100">
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto transition-all duration-150 ${
                      isSubmitting ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          ></path>
                        </svg>
                        Adding...
                      </span>
                    ) : (
                      "Add Branch"
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-150"
                    onClick={() => addBranchModalSetting()}
                    ref={cancelButtonRef}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default AddBranch;
